import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Send, CheckCircle, Home, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const products = [
  {
    id: 'monthly',
    name: 'Monthly Supply',
    price: 40.00,
    description: '30-day supply of CocoaLife™ truffles',
    servings: '30 truffles'
  },
  {
    id: '60day',
    name: '60-Day Supply',
    price: 59.00,
    description: '60-day supply - Best Value!',
    servings: '60 truffles',
    savings: 'Save $21'
  }
];

export default function TruffleOrderPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState('monthly');
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    shipping_address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    quantity: 1,
    notes: ''
  });

  const currentProduct = products.find(p => p.id === selectedProduct);
  const total = currentProduct.price * form.quantity;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.customer_name || !form.customer_email || !form.shipping_address || !form.city || !form.state || !form.zip_code) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/truffle-orders`, {
        ...form,
        product: selectedProduct
      });
      setSubmitted(true);
      toast.success('Order submitted! We will contact you soon.');
    } catch (error) {
      toast.error('Failed to submit order. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-24 md:py-32" data-testid="truffle-order-success">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Order Received!
            </h1>
            <p className="text-muted-foreground mb-4">
              Thank you for your CocoaLife™ order!
            </p>
            <p className="text-muted-foreground mb-8">
              We will contact you at <strong>{form.customer_email}</strong> with payment and shipping details.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              For questions: <a href="mailto:Cocoalife.min@gmail.com" className="text-accent hover:underline">Cocoalife.min@gmail.com</a>
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2">
                  <Home className="w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
              <Link to="/cocoalife">
                <Button className="btn-primary gap-2">
                  Learn More About CocoaLife™
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32" data-testid="truffle-order-page">
      <div className="container-custom">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-20 h-20 rounded-full bg-secondary/20 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-secondary" />
          </div>
          <h1 className="section-header mb-4">Order CocoaLife™</h1>
          <p className="section-subheader max-w-2xl mx-auto">
            Premium chocolate truffles with 102 minerals and 9 amino acid complexes. 
            Limited quantities due to shelf life.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          
          {/* Product Selection */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            <h2 className="text-xl font-bold">Select Your Supply</h2>
            
            {products.map((product) => (
              <Card 
                key={product.id}
                className={`cursor-pointer transition-all ${
                  selectedProduct === product.id 
                    ? 'border-secondary bg-secondary/10' 
                    : 'border-border/50 hover:border-secondary/50'
                }`}
                onClick={() => setSelectedProduct(product.id)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedProduct === product.id ? 'border-secondary' : 'border-muted-foreground'
                        }`}>
                          {selectedProduct === product.id && (
                            <div className="w-3 h-3 rounded-full bg-secondary" />
                          )}
                        </div>
                        <h3 className="font-bold text-lg">{product.name}</h3>
                        {product.savings && (
                          <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded font-bold">
                            {product.savings}
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm mt-2 ml-7">{product.description}</p>
                      <p className="text-muted-foreground text-xs mt-1 ml-7">{product.servings}</p>
                    </div>
                    <span className="text-2xl font-bold text-accent">${product.price}</span>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Product Image */}
            <Card className="bg-card border-secondary/30 overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_author-hub-59/artifacts/c4m7cv1f_Firefly_a%20beautify%20table%20with%20a%20fancy%20pl%2027140%2056A.jpg"
                alt="CocoaLife™ Truffles"
                className="w-full h-64 object-cover"
              />
              <CardContent className="p-4 text-center">
                <p className="text-sm text-muted-foreground">
                  Developed at Oregon State University • 102 Minerals • 9 Amino Acids
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Order Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="bg-card border-border/50">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-6">Shipping Information</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.customer_email}
                      onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Street Address *</Label>
                    <Input
                      id="address"
                      value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                      placeholder="123 Main St, Apt 4"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="City"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                        placeholder="State"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip">ZIP Code *</Label>
                      <Input
                        id="zip"
                        value={form.zip_code}
                        onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                        placeholder="12345"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        placeholder="USA"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="quantity">Quantity</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Special Instructions (Optional)</Label>
                    <Textarea
                      id="notes"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="Any dietary concerns or special requests..."
                      rows={3}
                    />
                  </div>

                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between mb-2">
                      <span>{currentProduct.name} x {form.quantity}</span>
                      <span className="font-bold">${total.toFixed(2)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Shipping costs will be calculated and sent to you via email.
                    </p>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary h-12 text-lg"
                      disabled={submitting}
                    >
                      {submitting ? (
                        'Submitting...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Order Request
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <p className="text-center text-sm text-muted-foreground mt-4">
              Questions? Email <a href="mailto:Cocoalife.min@gmail.com" className="text-accent hover:underline">Cocoalife.min@gmail.com</a>
            </p>
          </motion.div>
        </div>

        {/* Navigation Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-4 mt-16"
        >
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
          <Link to="/donate">
            <Button className="btn-primary gap-2">
              <Heart className="w-4 h-4" />
              Support / Donate
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
